import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useModalDismiss } from './use-modal-dismiss';
import { AdminRequestError } from './api';
import {
  getManualDialerSettings,
  updateManualDialerSettings,
  type DncPolicy,
  type ManualDialerMode,
  type ManualDialerSettingsPatch,
  type ManualDialerSettingsResponse,
} from './manual-dialer-api';

const MODES: { mode: ManualDialerMode; label: string; dot: string; blurb: string }[] = [
  {
    mode: 'off',
    label: 'Off',
    dot: 'bg-destructive',
    blurb: 'Nobody can mint a manual token, and /voice refuses any manual dial in flight.',
  },
  {
    mode: 'live',
    label: 'Live',
    dot: 'bg-emerald-500',
    blurb:
      'Every signed-in SDR can dial one matched lead at a time from /dial. Admins and SDR managers can also dial a number nobody has.',
  },
];

const dotFor = (mode: ManualDialerMode | null) =>
  MODES.find((m) => m.mode === mode)?.dot ?? 'bg-muted-foreground/40';

// Strictest first — the order the widening confirm compares by.
const DNC_POLICIES: { policy: DncPolicy; label: string; blurb: string }[] = [
  {
    policy: 'block_all',
    label: 'Never called',
    blurb: 'Every lead on the Do Not Contact list is refused, whatever blocked them. Today’s rule.',
  },
  {
    policy: 'allow_info_requests',
    label: 'Callable after an information request — three days on',
    blurb:
      'Only a lead who asked for information can be called through their block, and only once three days have passed since that call (the pools keep holding off for their 14). Every other block stays refused.',
  },
  {
    policy: 'allow_cooldowns',
    label: 'Callable on a cooldown — never someone who asked us to stop',
    blurb:
      'A lead blocked by a time-boxed verdict (asked for information, not interested, booked a meeting, wrong person, not a fit) can be called once the SDR has read why. A “do not contact” verdict, or a block with no call behind it, stays refused.',
  },
  {
    policy: 'allow_all',
    label: 'Always callable',
    blurb:
      'Every verdict can be called through once read — including someone who asked us to stop. Only a number blocked for every client stays refused.',
  },
];

// The mode the core is serving right now. Sibling of `useInboundStatus`, same rules: read
// once, updated by a save, and unknown rather than `off` when the read fails.
export function useManualDialerStatus(): {
  dot: string;
  label: string;
  apply: (mode: ManualDialerMode) => void;
} {
  const [mode, setMode] = useState<ManualDialerMode | null>(null);

  useEffect(() => {
    let live = true;
    getManualDialerSettings()
      .then((r) => {
        if (live) setMode(r.effective.mode);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return { dot: dotFor(mode), label: mode ?? '…', apply: setMode };
}

export function ManualDialerModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (mode: ManualDialerMode) => void;
}) {
  const [loaded, setLoaded] = useState<ManualDialerSettingsResponse | null>(null);
  const [mode, setMode] = useState<ManualDialerMode>('off');
  const [hoursGate, setHoursGate] = useState(true);
  const [transcripts, setTranscripts] = useState(false);
  const [anyForAll, setAnyForAll] = useState(false);
  const [dncPolicy, setDncPolicy] = useState<DncPolicy>('block_all');
  const [status, setStatus] = useState<string>('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dialog = useModalDismiss(onClose);

  useEffect(() => {
    let live = true;
    getManualDialerSettings()
      .then((r) => {
        if (!live) return;
        setLoaded(r);
        setMode(r.settings.mode);
        setHoursGate(r.settings.hours_gate);
        setTranscripts(r.settings.transcripts_enabled);
        setAnyForAll(r.settings.any_number_for_all === true);
        setDncPolicy(r.settings.dnc_policy ?? 'block_all');
        setStatus('');
      })
      .catch((err: unknown) => {
        if (live) setStatus(err instanceof AdminRequestError ? err.message : 'Failed to load');
      });
    return () => {
      live = false;
    };
  }, []);

  // A row saved live before this build has no filing pool, and a save is what creates
  // one — so "live with no pool" is offered as a change even when nothing was edited.
  const needsFilingPool = loaded?.settings.mode === 'live' && loaded.settings.pool_id == null;

  const patch = (): ManualDialerSettingsPatch => {
    if (!loaded) return {};
    const out: ManualDialerSettingsPatch = {};
    if (mode === 'live' && needsFilingPool) out.mode = 'live';
    if (mode !== loaded.settings.mode) out.mode = mode;
    if (out.mode === 'live') out.entitle_every_sdr = true;
    if (hoursGate !== loaded.settings.hours_gate) out.hours_gate = hoursGate;
    if (transcripts !== loaded.settings.transcripts_enabled) out.transcripts_enabled = transcripts;
    if (anyForAll !== (loaded.settings.any_number_for_all === true)) {
      out.any_number_for_all = anyForAll;
    }
    if (dncPolicy !== (loaded.settings.dnc_policy ?? 'block_all')) out.dnc_policy = dncPolicy;
    return out;
  };

  const dirty = loaded !== null && Object.keys(patch()).length > 0;

  const save = useCallback(() => {
    if (!loaded || !dirty) return;
    const changes = patch();
    // Live is the one direction with a blast radius: every SDR can start placing real,
    // recorded calls. Off needs no friction — it is the brake.
    // Also on a row already saved live but not yet set up: that Save creates the
    // filing pool, which is the moment every SDR actually gains the dialer.
    if (
      changes.mode === 'live' &&
      (loaded.settings.mode !== 'live' || needsFilingPool) &&
      !window.confirm(
        'Switch the manual dialer LIVE?\n\nEvery signed-in SDR can place real, recorded calls from /dial. Switching back to off is instant from this same switch. Continue?',
      )
    ) {
      return;
    }
    // Opening the pad to everyone is the other widening: any SDR can then place a
    // recorded call to a number no lead carries, and each new number becomes a
    // placeholder lead. Closing it again needs no friction.
    if (
      changes.any_number_for_all === true &&
      !window.confirm(
        'Let EVERY SDR dial a number nobody has?\n\nToday only admins and SDR managers can. Any SDR could then place a recorded call to any number; each new number becomes a placeholder lead under “Unmatched dials”. Switching it off again is instant. Continue?',
      )
    ) {
      return;
    }
    // Widening the Do Not Contact policy is the third widening: SDRs can then place a
    // recorded call to someone throxy blocked, after reading why. Narrowing needs none.
    if (
      changes.dnc_policy != null &&
      DNC_POLICIES.findIndex((p) => p.policy === changes.dnc_policy) >
        DNC_POLICIES.findIndex((p) => p.policy === (loaded.settings.dnc_policy ?? 'block_all')) &&
      !window.confirm(
        `Let SDRs call leads on the Do Not Contact list (“${DNC_POLICIES.find((p) => p.policy === changes.dnc_policy)?.label}”)?\n\nEvery SDR on /dial will see why a lead is blocked — the verdict, the call, the classifier’s reasoning — and must tick that they read it before “Call anyway” appears. ${
          // The transcript switch as it will stand after this save: with it off the
          // callback is recorded but never classified, so no block can move.
          transcripts
            ? 'The call is recorded and classified like any other.'
            : 'The call is recorded, but with manual transcripts off it is not classified — the block stays as it is.'
        } Switching back to “Never called” is instant. Continue?`,
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    updateManualDialerSettings(changes)
      .then((r) => onSaved(r.effective.mode))
      .catch((err: unknown) => {
        setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
        setSaving(false);
      });
  }, [loaded, dirty, needsFilingPool, mode, hoursGate, transcripts, anyForAll, dncPolicy, onSaved]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Manual dialer"
        className="bg-card flex max-h-[90vh] w-[min(28rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">Manual dialer</h2>
          <p className="text-muted-foreground text-sm">
            The SDR-typed dialer at /dial. Takes effect on the next token or dial — no deploy.
          </p>
        </div>

        {status && <p className="text-muted-foreground text-sm">{status}</p>}
        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {loaded && (
          <>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Manual dialer mode">
              {MODES.filter((m) => loaded.modes.includes(m.mode)).map((m) => (
                <label
                  key={m.mode}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                    mode === m.mode && 'border-foreground/40 bg-muted/40',
                  )}
                >
                  <input
                    type="radio"
                    name="manual-dialer-mode"
                    className="mt-1"
                    checked={mode === m.mode}
                    onChange={() => setMode(m.mode)}
                  />
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <span aria-hidden className={cn('h-2 w-2 rounded-full', m.dot)} />
                      {m.label}
                      {loaded.settings.mode === m.mode && (
                        <span className="text-muted-foreground text-xs font-normal">(current)</span>
                      )}
                    </span>
                    <span className="text-muted-foreground block text-xs leading-relaxed">
                      {m.blurb}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <p
              className={cn(
                'text-xs leading-relaxed',
                needsFilingPool
                  ? 'rounded-md border border-amber-600/30 bg-amber-500/5 p-3'
                  : 'text-muted-foreground',
              )}
            >
              {loaded.settings.pool_id == null
                ? 'Manual calls are filed under a hidden “Manual Dialer” pool, created the first time this is saved as Live. Press Save to create it — nobody can dial until it exists.'
                : `Manual calls are filed under the hidden “Manual Dialer” pool (#${loaded.settings.pool_id}). It never dials and never appears in the cockpit.`}
            </p>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={hoursGate}
                onChange={(ev) => setHoursGate(ev.target.checked)}
              />
              <span>
                <span className="font-medium">Refuse out-of-hours dials</span>
                <span className="text-muted-foreground block text-xs leading-relaxed">
                  Same working-hours window the pools use, on the lead’s local time.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={transcripts}
                onChange={(ev) => setTranscripts(ev.target.checked)}
              />
              <span>
                <span className="font-medium">Forward manual transcripts</span>
                <span className="text-muted-foreground block text-xs leading-relaxed">
                  Off: calls are recorded and transcribed, nothing downstream. On: leadflow,
                  dispositions and DNC — like every other call. Flip after the canary. A number
                  nobody has (the dial pad) never goes downstream either way.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={anyForAll}
                onChange={(ev) => setAnyForAll(ev.target.checked)}
              />
              <span>
                <span className="font-medium">Anyone can dial a number nobody has</span>
                <span className="text-muted-foreground block text-xs leading-relaxed">
                  Off: only admins and SDR managers get “Dial anyway” on a number that matches no
                  lead. On: every signed-in SDR does. A matched lead is dialable by everyone either
                  way.
                </span>
              </span>
            </label>

            {loaded.dnc_policies && (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium">Leads on the Do Not Contact list</legend>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  At every level except “Never called”, the SDR sees why the lead is blocked — the
                  verdict, the call it came from, the classifier’s reasoning, when the hold ends —
                  and must tick that they read it before “Call anyway” appears. A number blocked for
                  every client is never callable.
                </p>
                <div
                  className="flex flex-col gap-2"
                  role="radiogroup"
                  aria-label="Do Not Contact policy"
                >
                  {DNC_POLICIES.filter((p) => loaded.dnc_policies?.includes(p.policy)).map((p) => (
                    <label
                      key={p.policy}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                        dncPolicy === p.policy && 'border-foreground/40 bg-muted/40',
                      )}
                    >
                      <input
                        type="radio"
                        name="manual-dialer-dnc-policy"
                        className="mt-1"
                        checked={dncPolicy === p.policy}
                        onChange={() => setDncPolicy(p.policy)}
                      />
                      <span>
                        <span className="flex items-center gap-2 text-sm font-medium">
                          {p.label}
                          {(loaded.settings.dnc_policy ?? 'block_all') === p.policy && (
                            <span className="text-muted-foreground text-xs font-normal">
                              (current)
                            </span>
                          )}
                        </span>
                        <span className="text-muted-foreground block text-xs leading-relaxed">
                          {p.blurb}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {loaded.effective.mode !== loaded.settings.mode && (
              <div className="bg-muted/40 rounded-md border p-3 text-xs leading-relaxed">
                The serving node is currently on{' '}
                <span className="font-medium">{loaded.effective.mode}</span> while the saved row
                says <span className="font-medium">{loaded.settings.mode}</span> — a cache refresh
                is in flight or a node predates the row. Saving re-broadcasts.
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
